import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useIsUserViewingSpaceAsAnonQuery$data as AnonymousAccessSpaceQueryType,
	useIsUserViewingSpaceAsAnonQuery$variables as AnonymousAccessSpaceQueryVariables,
} from './__generated__/useIsUserViewingSpaceAsAnonQuery.graphql.ts';
import type { useIsUserViewingSpaceAsAnon_space$key } from './__generated__/useIsUserViewingSpaceAsAnon_space.graphql.ts';

export type AnonymousAccessSpaceFragmentRef = useIsUserViewingSpaceAsAnon_space$key;

type AnonymousAccessEligibility = {
	isUserViewingSpaceAsAnon: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

export const AnonymousAccessSpaceFragment: GraphQLTaggedNode = graphql`
	fragment useIsUserViewingSpaceAsAnon_space on Space {
		id
		currentUser {
			isAnonymouslyAuthorized
		}
	}
`;

export const useIsUserViewingSpaceAsAnon = ({
	spaceKey,
	skipQuery,
}: {
	spaceKey: string;
	skipQuery?: boolean;
}): AnonymousAccessEligibility => {
	const { data, loading, error } = useQueryInterop<
		AnonymousAccessSpaceQueryType,
		AnonymousAccessSpaceQueryVariables
	>(
		graphql`
			query useIsUserViewingSpaceAsAnonQuery($spaceKey: String) {
				space(key: $spaceKey) {
					...useIsUserViewingSpaceAsAnon_space @relay(mask: false)
				}
			}
		`,
		{
			variables: { spaceKey },
			skip: skipQuery || !spaceKey,
		},
	);

	// For restricted spaces the preloader's query returns a 403 which Apollo evicts from
	// its cache. This leaves `useQueryInterop` in a perpetual `loading: true` state during
	// SSR, while on the client the same query resolves with `loading: false` and
	// `space: null`. The resulting mismatch causes a hydration error in the SideNav footer.
	//
	// The preloader detects this case and persists the space key in a global
	// (`__RESTRICTED_SPACES_FROM_PRELOAD__`). When present, we short-circuit here so both
	// SSR and client agree on `{ loading: false, isUserViewingSpaceAsAnon: false }`.
	const restrictedSpaces =
		typeof window !== 'undefined'
			? (window as any).__RESTRICTED_SPACES_FROM_PRELOAD__
			: (globalThis as any).__RESTRICTED_SPACES_FROM_PRELOAD__;

	if (restrictedSpaces?.[spaceKey]) {
		return { isUserViewingSpaceAsAnon: false, loading: false, error: undefined };
	}

	return {
		isUserViewingSpaceAsAnon: Boolean(data?.space?.currentUser?.isAnonymouslyAuthorized),
		loading,
		error,
	};
};

export const useIsUserViewingSpaceAsAnonFromFragment = ({
	spaceKey,
	spaceFragmentRef,
}: {
	spaceKey: string;
	spaceFragmentRef: AnonymousAccessSpaceFragmentRef;
}): AnonymousAccessEligibility => {
	const space = useFragment(AnonymousAccessSpaceFragment, spaceFragmentRef);
	const restrictedSpaces = (window as any).__RESTRICTED_SPACES_FROM_PRELOAD__;

	return {
		isUserViewingSpaceAsAnon: restrictedSpaces?.[spaceKey]
			? false
			: Boolean(space.currentUser?.isAnonymouslyAuthorized),
		loading: false,
		error: undefined,
	};
};
